import { Field, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity, Company, generateEntityId, RelatedEntity, User } from '@ztechapps/common.contract'
import { SchoolInviteStatusEnum } from '../enums/SchoolInviteStatusEnum'

/**
 * A single-use, time-boxed invitation for a registered teacher to join a school's shared
 * credit pool. One outstanding invite per (school, invitee) pair — re-inviting replaces the
 * existing row rather than issuing a second one (upsert on that pair, see
 * InviteTeachersToSchool). `tokenHash` is the only persisted form of the token; the raw value
 * exists only in the emailed URL and is never written to the database or logs.
 */
@ObjectType()
@InputType('SchoolInviteInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'schoolinvites' } })
export class SchoolInvite extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('scin') })
  declare _id: string

  @Field(() => Company)
  @Prop({ type: RelatedEntity })
  school: ReturnType<() => Company>

  @Field(() => User)
  @Prop({ type: RelatedEntity })
  invitee: ReturnType<() => User>

  // SHA-256 hash of the raw token, never the token itself. No `@Field` — never exposed
  // through GraphQL.
  @Prop({ type: String, required: true })
  tokenHash: string

  @Field(() => Date)
  @Prop({ type: Date, required: true })
  expiresAt: Date

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  redeemedAt?: Date

  /**
   * Derived, resolver-only in intent — `SchoolInviteFieldResolver.status` computes it from
   * `redeemedAt`/`expiresAt` on every read, overriding whatever is stored. But this class carries
   * no `@Prop` of its OWN for `status` only because it doesn't need to redeclare one: `status` is
   * inherited from `BaseEntity` WITH a real `@Prop({ type: String })` (`BaseEntity.ts:32-34`), so
   * it remains a genuine persisted schema path — a write CAN set it, that value round-trips
   * through the database, and the field resolver silently overrides it back to the derived value
   * on every read. `declare` here only re-narrows the TS type to `SchoolInviteStatusEnum` for the
   * `@Field` override; it does not remove the inherited `@Prop`.
   */
  @Field(() => SchoolInviteStatusEnum, { nullable: true })
  declare status?: SchoolInviteStatusEnum
}
